import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { isRequestActive, loadAndTrack } from "redux-request-loading";
import { Clinic, FixedLengthChildAppointment, NonFixedLengthChildAppointment } from "@teamprevent/common/calendar/models";

import { ApplicationState } from "../../applicationState";
import * as selectors from "../selectors";

import { LOAD_CLINIC } from "./actionTypes";
import { clinicApi } from "./clinicApi";
import { loadClinicSuccess } from "./actions";

type useClinicAndAppointmentReturn =
    {
        clinic: Clinic;
        appointment: FixedLengthChildAppointment | NonFixedLengthChildAppointment;
        isLoading: false;
        isError: false;
        refresh: () => void;
        isLoaded: true;
    }
    | { clinic?: undefined; appointment?: undefined; isLoading: boolean; isError: boolean; refresh: () => void; isLoaded: false };

type useClinicAndAppointmentArguments =
    { clinicId: string; appointmentId: string };

/*
* This hook is used to load the clinic and one of it's child appointments details from the server
* Usually these args can be passed in from the route selectors.
* If you don't need the clinic details, you can pass `enable: false` to the hook to disable the loading of the clinic
* until you need it by calling `refresh()`
*/
export const useClinicAndAppointment = (args: useClinicAndAppointmentArguments, { enable }: { enable?: boolean } = {}): useClinicAndAppointmentReturn => {
    if (args.clinicId === undefined && args.appointmentId === undefined) {
        throw new Error("useClinicAndAppointment can only be used with both `clinicId` and `appointmentId` params");
    }

    const dispatch = useDispatch();
    const clinics = useSelector(selectors.clinicsSelector);
    const clinic = clinics.find(c => c.id === args.clinicId);
    const appointment = clinic?.appointments.find(a => a.id === args.appointmentId);
    const requestKey = `${LOAD_CLINIC}_${args.clinicId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);
    const refresh = React.useCallback(async () => {
        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, requestKey, (async () => {
                const result = await clinicApi.get(args.clinicId);
                dispatch(loadClinicSuccess(result));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [dispatch, args.clinicId, requestKey]);
    React.useEffect(() => {
        if ((clinic !== undefined && appointment === undefined)) {
            // We don't expect to have a clinic without a child appointment
            setIsError(true);
            return;
        } else if (enable === false
                   || isLoading
                   || isError
                   || (clinic !== undefined && appointment !== undefined)) {
            return;
        }

        refresh();
    }, [enable, isLoading, clinic, appointment, refresh, isError]);

    if (clinic !== undefined && appointment !== undefined) {
        return { clinic, appointment, isLoading: false, isError: false, refresh, isLoaded: true };
    } else {
        return { isLoading, isError, refresh, isLoaded: false };
    }
};
